import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Loader2, Lock, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from '@/components/ui/progress';
import { 
  ACHIEVEMENT_DEFINITIONS, 
  AchievementDefinition 
} from './achievementDefinitions';
import { useSystemAchievementOverrides } from '@/hooks/useSystemAchievementOverrides';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SellerAchievement {
  id: string;
  seller_id: string;
  achievement_type: string;
  unlocked_at: string;
  notified: boolean;
}

interface SellerStats {
  totalVentas: number;
  ventasMes: number;
  totalCanjes: number;
  totalAhorros: number;
  trainingCompleted: boolean;
  ranking: number;
}

interface MemberAchievementsViewProps {
  sellerId: string;
}

export const MemberAchievementsView: React.FC<MemberAchievementsViewProps> = ({ sellerId }) => {
  const [achievements, setAchievements] = useState<SellerAchievement[]>([]);
  const [stats, setStats] = useState<SellerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { 
    getMergedAchievements, 
    getAchievementPoints, 
    isAchievementActive,
    loading: overridesLoading 
  } = useSystemAchievementOverrides();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch achievements for this seller from seller_achievements
        const { data: achievementsData, error: achievementsError } = await supabase
          .from('seller_achievements')
          .select('*')
          .eq('seller_id', sellerId)
          .order('unlocked_at', { ascending: false });

        if (achievementsError) throw achievementsError;
        
        const fetchedAchievements: SellerAchievement[] = achievementsData || [];

        // Fetch cycle milestones from seller_cycle_stats
        const currentCycle = new Date().toISOString().slice(0, 7); // YYYY-MM
        const { data: cycleStats } = await supabase
          .from('seller_cycle_stats')
          .select('*')
          .eq('seller_id', sellerId)
          .eq('cycle_month_year', currentCycle)
          .maybeSingle();

        // Add cycle milestones to achievements list if achieved
        if (cycleStats) {
          const now = new Date().toISOString();
          const addCycleMilestone = (type: string, achieved: boolean) => {
            if (achieved && !fetchedAchievements.some(a => a.achievement_type === type)) {
              fetchedAchievements.push({
                id: `cycle-${type}`,
                seller_id: sellerId,
                achievement_type: type,
                unlocked_at: cycleStats.updated_at || now,
                notified: true
              });
            }
          };
          
          addCycleMilestone('first_sale', cycleStats.first_sale_achieved);
          addCycleMilestone('first_redemption', cycleStats.first_redemption_achieved);
          addCycleMilestone('strong_start', cycleStats.strong_start_achieved);
          addCycleMilestone('consistency', cycleStats.consistency_achieved);
          addCycleMilestone('volume_bronce_10', (cycleStats.bronce_sales || 0) >= 10);
          addCycleMilestone('volume_plata_10', (cycleStats.plata_sales || 0) >= 10);
          addCycleMilestone('volume_oro_10', (cycleStats.oro_sales || 0) >= 10);
          addCycleMilestone('sales_streak_3', (cycleStats.consecutive_sales_count || 0) >= 3);
        }

        setAchievements(fetchedAchievements);

        // Fetch ranking stats to calculate progress
        const { data: rankingData, error: rankingError } = await supabase.rpc('get_seller_ranking_stats');
        if (rankingError) throw rankingError;

        const sellerData = rankingData?.find((s: any) => s.seller_id === sellerId);
        const ranking = rankingData?.findIndex((s: any) => s.seller_id === sellerId) + 1 || 0;

        if (sellerData) {
          setStats({
            totalVentas: sellerData.total_ventas || 0,
            ventasMes: sellerData.total_ventas || 0, // Monthly stats would need separate query
            totalCanjes: sellerData.total_canjes || 0,
            totalAhorros: sellerData.total_ahorros || 0,
            trainingCompleted: sellerData.training_completed || false,
            ranking,
          });
        } else {
          setStats({
            totalVentas: 0,
            ventasMes: 0,
            totalCanjes: 0,
            totalAhorros: 0,
            trainingCompleted: false,
            ranking: 0,
          });
        }
      } catch (error) {
        console.error('Error fetching achievements:', error);
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchData();
    }
  }, [sellerId]);

  const mergedAchievements = getMergedAchievements();
  const activeAchievements = mergedAchievements.filter(a => a.isActive);
  
  const unlockedTypes = new Set(achievements.map(a => a.achievement_type));
  const totalPoints = achievements.reduce((sum, a) => {
    if (!isAchievementActive(a.achievement_type)) return sum;
    return sum + getAchievementPoints(a.achievement_type);
  }, 0);
  const maxPoints = activeAchievements.reduce((sum, a) => sum + a.points, 0);
  const progressPercent = maxPoints > 0 ? (totalPoints / maxPoints) * 100 : 0;

  const getProgress = (def: AchievementDefinition): { current: number; target: number } | null => {
    if (!stats) return null;

    switch (def.type) {
      case 'first_sale':
        return { current: Math.min(stats.totalVentas, 1), target: 1 };
      case 'sales_10':
        return { current: Math.min(stats.ventasMes, 10), target: 10 };
      case 'sales_50':
        return { current: Math.min(stats.totalVentas, 50), target: 50 };
      case 'sales_100':
        return { current: Math.min(stats.totalVentas, 100), target: 100 };
      case 'redemptions_50':
        return { current: Math.min(stats.totalCanjes, 50), target: 50 };
      case 'savings_100k':
        return { current: Math.min(stats.totalAhorros, 100000), target: 100000 };
      case 'savings_500k':
        return { current: Math.min(stats.totalAhorros, 500000), target: 500000 };
      case 'training_complete':
        return { current: stats.trainingCompleted ? 1 : 0, target: 1 };
      case 'podium':
        return stats.ranking > 0 && stats.ranking <= 3 ? { current: 1, target: 1 } : { current: 0, target: 1 };
      case 'first_place':
        return stats.ranking === 1 ? { current: 1, target: 1 } : { current: 0, target: 1 };
      default:
        return null;
    }
  };

  const formatProgress = (def: AchievementDefinition, progress: { current: number; target: number }): string => {
    if (def.type.includes('savings')) {
      const formatCurrency = (v: number) => v >= 1000 ? `₡${(v/1000).toFixed(0)}K` : `₡${v}`;
      return `${formatCurrency(progress.current)} / ${formatCurrency(progress.target)}`;
    }
    return `${progress.current} / ${progress.target}`;
  };

  const unlockedAchievements = activeAchievements.filter(def => unlockedTypes.has(def.type));
  const lockedAchievements = activeAchievements.filter(def => !unlockedTypes.has(def.type));

  if (loading || overridesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <span className="text-white font-medium">Progreso de Logros</span>
          </div>
          <span className="text-amber-400 font-bold">{totalPoints} pts</span>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/60">
              {unlockedAchievements.length} de {activeAchievements.length} logros
            </span>
            <span className="text-white/60">{progressPercent.toFixed(0)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-white/10" />
        </div>
      </div>

      {/* Unlocked Achievements */}
      {unlockedAchievements.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-white/80 text-sm font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            Logros Desbloqueados
          </h4>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            <TooltipProvider>
              {unlockedAchievements.map((def) => {
                const achievement = achievements.find(a => a.achievement_type === def.type);
                return (
                  <Tooltip key={def.type}>
                    <TooltipTrigger asChild>
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-2 border border-amber-500/30 text-center cursor-pointer hover:scale-105 transition-transform"
                      >
                        <span className="text-2xl block">{def.badge}</span>
                        <span className="text-white/70 text-[10px] block truncate mt-1">{def.name}</span>
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-900 border-white/20 p-3 max-w-[200px]">
                      <p className="font-medium text-white">{def.name}</p>
                      <p className="text-white/60 text-xs mt-1">{def.description}</p>
                      <p className="text-amber-400 text-xs mt-1">+{getAchievementPoints(def.type)} puntos</p>
                      {achievement && (
                        <p className="text-white/40 text-xs mt-1">
                          {format(new Date(achievement.unlocked_at), "d 'de' MMMM, yyyy", { locale: es })}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Locked Achievements */}
      {lockedAchievements.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-white/80 text-sm font-medium flex items-center gap-2">
            <Lock className="w-4 h-4 text-white/40" />
            Por Desbloquear
          </h4>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            <TooltipProvider>
              {lockedAchievements.map((def) => {
                const progress = getProgress(def);
                return (
                  <Tooltip key={def.type}>
                    <TooltipTrigger asChild>
                      <motion.div
                        className="bg-white/5 rounded-xl p-2 border border-white/10 text-center cursor-pointer hover:bg-white/10 transition-colors opacity-60"
                      >
                        <span className="text-2xl block grayscale">{def.badge}</span>
                        <span className="text-white/40 text-[10px] block truncate mt-1">{def.name}</span>
                        {progress && (
                          <div className="mt-1">
                            <Progress 
                              value={(progress.current / progress.target) * 100} 
                              className="h-1 bg-white/10" 
                            />
                          </div>
                        )}
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-900 border-white/20 p-3 max-w-[200px]">
                      <p className="font-medium text-white">{def.name}</p>
                      <p className="text-white/60 text-xs mt-1">{def.requirement}</p>
                      <p className="text-amber-400 text-xs mt-1">+{getAchievementPoints(def.type)} puntos</p>
                      {progress && (
                        <p className="text-blue-400 text-xs mt-1">
                          Progreso: {formatProgress(def, progress)}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Empty State */}
      {achievements.length === 0 && (
        <div className="text-center py-6">
          <Trophy className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm">Este vendedor aún no tiene logros desbloqueados</p>
          <p className="text-white/30 text-xs mt-1">Los logros se desbloquean al cumplir metas de ventas, canjes y más</p>
        </div>
      )}
    </div>
  );
};

export default MemberAchievementsView;
